import { browserLogger } from '@newfront-insurance/dd-rum';

import { NOVA_NOTIFICATION_ICON } from '../lib/notification-icon';

/**
 * Stateless facade over the browser Notification API.
 *
 * Deliberately holds NO enablement state: whether a notification should be
 * shown (feature flag, server-persisted user setting, permission) is decided
 * by the caller from live sources at display time. Keeping copies of those
 * gates here is what previously froze stale values in for a whole session.
 */
export const browserNotificationManager = {
  isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  },

  getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  },

  hasPermission(): boolean {
    return this.getPermission() === 'granted';
  },

  async requestPermission(): Promise<NotificationPermission> {
    const currentPermission = this.getPermission();
    if (currentPermission !== 'default') {
      return currentPermission;
    }

    try {
      const permission = await Notification.requestPermission();
      browserLogger.info('[Nova Notifications] Permission requested', { permission });
      return permission;
    } catch (error) {
      browserLogger.error('[Nova Notifications] Error requesting permission', { error });
      return 'denied';
    }
  },

  /**
   * Displays a chat notification. Returns true only when the Notification was
   * actually constructed — callers use this to roll back their dedupe claim on
   * failure so delivery can retry on a later emission.
   */
  showChatNotification({
    body,
    chatUuid,
    onClick,
  }: {
    body: string;
    chatUuid: string;
    onClick?: (chatUuid: string) => void;
  }): boolean {
    if (!this.hasPermission()) {
      browserLogger.warn('[Nova Notifications] No permission to show notifications');
      return false;
    }

    try {
      const notification = new Notification('Nova: New messages available', {
        body,
        icon: NOVA_NOTIFICATION_ICON,
        // One notification per chat; renotify re-alerts when a newer message
        // replaces an unclicked one instead of swapping it in silently.
        tag: `nova-chat-${chatUuid}`,
        badge: NOVA_NOTIFICATION_ICON,
        requireInteraction: false,
        ...({ renotify: true } as NotificationOptions),
      });

      notification.onclick = () => {
        window.focus();
        onClick?.(chatUuid);
        notification.close();
      };

      browserLogger.debug('[Nova Notifications] Browser notification displayed', { body, chatUuid });
      return true;
    } catch (error) {
      browserLogger.error('[Nova Notifications] Failed to display notification', { error, chatUuid });
      return false;
    }
  },
};

export type BrowserNotificationManager = typeof browserNotificationManager;
